import Dexie, { type EntityTable } from 'dexie';
import storage from '../../../../hooks/useStorage/useStorage';
import { OfflineDatabaseBrandGeneralSettings } from './types/offlineDatabaseBrandGeneralSettings.types';
import { OfflineDatabaseCatalog } from './types/offlineDatabaseCatalog.types';
import { OfflineDatabaseCategory } from './types/offlineDatabaseCategory.types';
import { OfflineDatabaseClassification } from './types/offlineDatabaseClassification.types';
import { OfflineDatabaseClassificationType } from './types/offlineDatabaseClassificationType.types';
import { OfflineDatabaseCollection } from './types/offlineDatabaseCollection.types';
import { OfflineDatabaseColor } from './types/offlineDatabaseColor.types';
import { OfflineDatabaseCustomer } from './types/offlineDatabaseCustomer.types';
import { OfflineDatabaseDeliveryWindow } from './types/offlineDatabaseDeliveryWindow.types';
import { OfflineDatabaseFutureStock } from './types/offlineDatabaseFutureStock.types';
import { OfflineDatabaseKit } from './types/offlineDatabaseKit.types';
import { OfflineDatabaseMeta } from './types/offlineDatabaseMeta.types';
import { OfflineDatabaseOrder } from './types/offlineDatabaseOrder.types';
import { OfflineDatabasePack } from './types/offlineDatabasePack.types';
import { OfflineDatabasePriceTable } from './types/offlineDatabasePriceTable.types';
import { OfflineDatabaseProduct } from './types/offlineDatabaseProduct.types';
import { OfflineDatabaseProductColor } from './types/offlineDatabaseProductColor.types';
import { OfflineDatabaseProductColorPriceInfo } from './types/offlineDatabaseProductColorPriceInfo.types';
import { OfflineDatabaseReferenceColor } from './types/offlineDatabaseReferenceColor.types';
import { OfflineDatabaseSalesRepresentative } from './types/offlineDatabaseSalesRepresentative.types';
import { OfflineDatabaseSeller } from './types/offlineDatabaseSeller.types';
import { OfflineDatabaseSize } from './types/offlineDatabaseSize.types';
import { OfflineDatabaseSku } from './types/offlineDatabaseSku.types';
import { OfflineDatabaseStock } from './types/offlineDatabaseStock.types';
import { addDecryptHookToOfflineDatabase } from './utils/addDecryptHookToOfflineDatabase';
import { addEncryptMiddlewareToOfflineDatabase } from './utils/addEncryptMiddlewareToOfflineDatabase';

export type OfflineDatabase = Dexie & {
  skus: EntityTable<OfflineDatabaseSku, 'id'>;
  kits: EntityTable<OfflineDatabaseKit, 'id'>;
  packs: EntityTable<OfflineDatabasePack, 'id'>;
  sizes: EntityTable<OfflineDatabaseSize, 'id'>;
  colors: EntityTable<OfflineDatabaseColor, 'id'>;
  stocks: EntityTable<OfflineDatabaseStock, 'id'>;
  orders: EntityTable<OfflineDatabaseOrder, 'code'>;
  sellers: EntityTable<OfflineDatabaseSeller, 'id'>;
  catalogs: EntityTable<OfflineDatabaseCatalog, 'id'>;
  products: EntityTable<OfflineDatabaseProduct, 'id'>;
  customers: EntityTable<OfflineDatabaseCustomer, 'id'>;
  categories: EntityTable<OfflineDatabaseCategory, 'id'>;
  priceTables: EntityTable<OfflineDatabasePriceTable, 'id'>;
  collections: EntityTable<OfflineDatabaseCollection, 'id'>;
  futureStocks: EntityTable<OfflineDatabaseFutureStock, 'id'>;
  productColors: EntityTable<OfflineDatabaseProductColor, 'id'>;
  referenceColors: EntityTable<OfflineDatabaseReferenceColor, 'id'>;
  deliveryWindows: EntityTable<OfflineDatabaseDeliveryWindow, 'id'>;
  classifications: EntityTable<OfflineDatabaseClassification, 'id'>;
  classificationTypes: EntityTable<OfflineDatabaseClassificationType, 'id'>;
  salesRepresentatives: EntityTable<OfflineDatabaseSalesRepresentative, 'id'>;
  brandGeneralSettings: EntityTable<OfflineDatabaseBrandGeneralSettings, 'id'>;
  productColorsPriceInfo: EntityTable<OfflineDatabaseProductColorPriceInfo, any>; // TODO: DEXIE AINDA NÃO ACEITA TIPAGEM PARA COMPOUND PRIMARY KEY>;
  meta: EntityTable<OfflineDatabaseMeta, 'key'>;
};

let offlineDatabase: OfflineDatabase;

const getIndexDbNameToUser = () => {
  return `offline-database-${storage.getTeamId()}`;
};

const existsIndexDbToUser = async () => {
  const indexDbName = getIndexDbNameToUser();

  try {
    if (!indexedDB.databases) {
      return await Dexie.exists(indexDbName);
    }

    const databases = await indexedDB.databases();
    return databases.some((database) => database.name === indexDbName);
  } catch (_) {
    return false;
  }
};

const initializeOfflineDatabase = () => {
  if (offlineDatabase) {
    offlineDatabase.close({
      disableAutoOpen: true,
    });
  }

  offlineDatabase = new Dexie(getIndexDbNameToUser()) as OfflineDatabase;

  offlineDatabase
    .version(5)
    .stores({
      skus: 'id',
      packs: 'id',
      sizes: 'id',
      stocks: 'id',
      colors: 'id',
      categories: 'id',
      kits: 'id, code',
      collections: 'id',
      futureStocks: 'id',
      sellers: 'id, name',
      productColors: 'id',
      products: 'id, code',
      orders: '++code, id',
      referenceColors: 'id',
      deliveryWindows: 'id',
      priceTables: 'id, name',
      catalogs: 'id, position',
      classificationTypes: 'id',
      classifications: 'id, code',
      customers: 'id, commercialName',
      salesRepresentatives: 'id, name, code',
      brandGeneralSettings: 'id, brandId',
      productColorsPriceInfo: '[productColorId+priceTableId]',
      meta: 'key',
    })
    .upgrade((tx) => {
      return tx.table('meta').put({ key: 'canEnterInOfflineMode', value: false });
    });

  addDecryptHookToOfflineDatabase(offlineDatabase);
  addEncryptMiddlewareToOfflineDatabase(offlineDatabase);
};

export { existsIndexDbToUser, initializeOfflineDatabase, offlineDatabase };
